import React from 'react'
import ResizeObserver from 'resize-observer-polyfill'
import Option from '../option/Option.container'
import Node from './Node.container'
import { CHECKED } from '../checkbox/Checkbox'
import { applySearch } from '../behaviour/search'
import {
  updateSelection,
  clearAll,
  setStatusFromValues
} from '../behaviour/selection'
import {
  walk,
  getNodeByPath
} from '../util/treeUtils'
import {
  toggleNodeIsOpen,
  updateSlots
} from '../util/shadowTreeUtils'
import { StyledTreeView, StyledNoSearchResults } from './TreeView.styles'
import {
  OPENING,
  OPENED,
  CLOSING
} from '../constants'

/**
 * TreeView
 *
 * Displays collapsible nodes/parents.
 *
 * As it may display large data sets some caution is advised, hence data is stored
 * as an instance field, not in state. Render updates are managed entirely manually.
 *
 * State lives in shadowTree, which is a copy of the data tree structure plus props
 * for state/display, to not pollute the original data.
 *
 * Built with reusablity in mind - without actually implementing it though, as it's
 * presently not required.
 * Logic like search and updateSelection could be handled by a parent component as well.
 * <Option> could be inserted via a render callback.
 *
 */
export default class TreeView extends React.Component {
  constructor(props) {
    super(props)

    this.isOpen = false
    this.isSearching = false
    this.isNoSearchResults = false

    this.searchTerm = ''
    this.clickedNode = null
    this.clickedNodeGlobalSlot = 0
    this.numNodesShown = 0
    this.values = []

    this.shadowTreeBeforeSearch = null
    this.scrollYBeforeSearch = null
    this.resizeObserver = new ResizeObserver(this.onResize) // Not throttled intentionally, just wouldn't look smooth enough
    this.treeEl = null
    this.width = 0
    this.timeouts = {}

    this.shadowTree = props.shadowTree
    updateSlots(this.shadowTree)
  }

  onRefTree = el => {
    if (el && el.attrs) return // @TODO remove eventually - only for styled-components v.3 / v.4 switch
    this.treeEl = el
  }

  setInitialState() { // Closes and hides all except top level
    walk(this.shadowTree, node => {
      node.isShown = (node.path.length < 3)
      if (node.isParent) node.isOpen = (node.path.length < 2)
    })
    updateSlots(this.shadowTree)
  }

  componentDidMount() {
    this.setInitialState()
    this.onDidRender({})
    this.forceUpdate() // Sets initial styles once, prevents flicker on first opening
    this.isOpen = false
  }

  componentWillUnmount() {
    for(const t in this.timeouts) clearTimeout(this.timeouts[t])
    this.resizeObserver.disconnect()
  }

  shouldComponentUpdate(nextProps) {
    const should = nextProps.searchTerm !== this.searchTerm ||
      nextProps.isOpen !== this.props.isOpen ||
      nextProps.status !== this.props.status ||
      nextProps.hasScrollbar !== this.props.hasScrollbar ||
      nextProps.values !== this.props.values

    return should
  }

  componentDidUpdate(prevProps, prevState) {
    this.onDidRender(prevProps)
  }

  onDidRender(prevProps) {
    // Performance monitoring
    // const time = Date.now() - this.timestampRender; console.log('r', time)

    if (this.props.isOpen) {
      this.props.onChangeHeight(this.numNodesShown)
    }

    // Activates scrollbars only when opened, to prevent flicker during transition
    const { status } = this.props

    if (status === OPENED || status === OPENING) {
      this.treeEl.style.overflow = this.props.hasScrollbar ? 'auto' : 'hidden'
      this.treeEl.style.overflowX = 'hidden'
      this.resizeObserver.observe(this.treeEl)
    } else if (status === CLOSING) {
      this.treeEl.style.overflow = 'hidden'
      this.resizeObserver.unobserve(this.treeEl)
    }

    // Values -> selection
    if (this.props.values === null && this.values.length) {
      clearAll(this.shadowTree)
      if (!this.isOpen) this.setInitialState()
    } else if (this.props.values && this.props.values.length !== this.values.length) {
      setStatusFromValues(this.shadowTree, this.props.values)
      this.values = this.props.values || []
    }
  }

  onClickOptionCheckbox = (path) => {
    updateSelection(this.shadowTree, path)

    const values = []

    walk(this.shadowTree, node => {
      if (node.checkboxStatus === CHECKED && !node.isParent) {
        values.push(node.value)
      }
    })
    this.values = values
    this.props.onChange(values)
    this.forceUpdate()
  }

  onClickParentNode = (path) => {
    const clickedNode = getNodeByPath(this.shadowTree, path)

    toggleNodeIsOpen(this.shadowTree, clickedNode)

    this.clickedNode = clickedNode
    this.clickedNodeGlobalSlot = clickedNode.globalSlot
    this.forceUpdate()
  }

  onResize = () => {
    const { offsetWidth } = this.treeEl

    if (this.width !== offsetWidth) {
      this.width = offsetWidth
      this.forceUpdate()
    }
  }

  applyFilters() { // Only one filter for now: Search
    const { searchTerm } = this.props

    if (searchTerm !== this.searchTerm) {
      this.isSearching = (searchTerm !== '')

      // User begins searching, make a copy of shadowTree to restore view state later when finished
      if (this.searchTerm === '' && this.treeEl) {
        this.shadowTreeBeforeSearch = JSON.parse(JSON.stringify(this.shadowTree))
        this.scrollYBeforeSearch = this.treeEl.scrollTop
      }

      let matches

      if (this.isSearching) {
        matches = applySearch(this.shadowTree, searchTerm)
        
        clearTimeout(this.timeouts.restoreScroll)
        this.scroll(0)
      } else if (this.shadowTreeBeforeSearch){
        this.restoreStateFromBeforeSearch()
      }
      else{
        this.setInitialState()
      }
      
      this.isNoSearchResults = (matches && matches.length === 0)
      this.searchTerm = searchTerm

      updateSlots(this.shadowTree)
    }
  }

  restoreStateFromBeforeSearch(){
    walk(this.shadowTreeBeforeSearch, node => {
      const currentNode = getNodeByPath(this.shadowTree, node.path)

      node.checkboxStatus = currentNode.checkboxStatus
      if (node.isParent) {
        node.numSelected = currentNode.numSelected
      }
    })
    this.shadowTree = this.shadowTreeBeforeSearch
    this.timeouts.restoreScroll = setTimeout(() => this.scroll(this.scrollYBeforeSearch), 0)
  }

  scroll(y) {
    if (this.treeEl) this.treeEl.scrollTop = y
  }

  //  Renders children inside parents recursively
  renderTree(tree, isOpenChanged) {
    const isNewlyOpened = (this.props.isOpen && isOpenChanged)

    const components = []

    tree.forEach(node => {
      if (!node.isShown) return

      const isOpenAnimIn = isNewlyOpened && node.globalSlot < this.props.numNodesVisibleMax

      this.numNodesShown++

      const nodeContent = <Option
        searchTermIndex={node.searchTermIndex}
        searchTermLength={this.props.searchTerm.length}
        title={node.title}
        path={node.path}
        checkboxStatus={node.checkboxStatus}
        onClickCheckbox={this.onClickOptionCheckbox}
        isParent={node.isParent}
        numSelected={node.isParent && node.numSelected}
        isOpenAnimIn={isOpenAnimIn}
      />

      components.push(
        <Node
          content={nodeContent}
          key={node.key}
          onClick={node.isParent && this.onClickParentNode}
          path={node.path}
          slot={node.slot}
          globalSlot={node.globalSlot}
          isShown={node.isShown}
          isParent={node.isParent}
          isOpen={node.isOpen}
          title={node.title}
          delay={node.globalSlot - this.clickedNodeGlobalSlot}
          isOpenAnimIn={isOpenAnimIn}
          isSearching={this.isSearching}
          width={this.width}
        >
          {node.isParent && this.renderTree(node.children, isOpenChanged)}
        </Node>
      )
    })

    return components
  }

  render() {
    const isOpenChanged = (this.props.isOpen !== this.isOpen)

    this.isOpen = this.props.isOpen

    this.numNodesShown = 0
    this.width = this.treeEl ? this.treeEl.offsetWidth : 0

    this.applyFilters()

    // Performance monitoring
    // this.timestampRender = Date.now()

    return (
      <StyledTreeView data-name='TREE' innerRef={this.onRefTree} ref={this.onRefTree}>

        {this.renderTree(this.shadowTree, isOpenChanged)}

        {this.isNoSearchResults && <StyledNoSearchResults>{this.props.searchNoResults}</StyledNoSearchResults>}

      </StyledTreeView>
    )
  }
}
